import { Box } from '@material-ui/core';
import { Currency, EditableField, LenderName } from '@nemuru-eng/js-api-client';

import {
  FinancingConditionsSelection,
  useEditFinancingSimulator,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditFinancingStep/hooks/useEditFinancingSimulator';
import { EditVehicleAmountControl } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditVehicleAmountControl';
import { VehicleAmountRange } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/vehicleAmountRange.map';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export type { FinancingConditionsSelection };

interface EditFinancingStepProps {
  currency: Currency;
  editableFields: EditableField[];
  financingDefaults: FinancingLiveSummaryData;
  isEnabled: boolean;
  needsFinancingReselection: boolean;
  showVehicleAmountControl: boolean;
  vehicleAmountRange: VehicleAmountRange;
  selectedLenderName: LenderName | undefined;
  onFinancingConditionsSelected: (selection: FinancingConditionsSelection) => void;
}

export const EditFinancingStep = ({
  currency,
  editableFields,
  financingDefaults,
  isEnabled,
  needsFinancingReselection,
  showVehicleAmountControl,
  vehicleAmountRange,
  selectedLenderName,
  onFinancingConditionsSelected,
}: EditFinancingStepProps) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const { isSimulatorLoading, hasSimulatorMounted, simulatorMountId } = useEditFinancingSimulator({
    editableFields,
    lenderName: selectedLenderName,
    financingDefaults,
    isEnabled,
    needsFinancingReselection,
    showVehicleAmountControl,
    vehicleAmountRange,
    onFinancingConditionsSelected,
  });

  if (!isEnabled) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <Font variant="body2" color="textSecondary">
          <Text tId="SIMULATOR_DISABLED" />
        </Font>
      </Box>
    );
  }

  return (
    <Box width="100%" display="flex" flexDirection="column" gridGap={16}>
      <Box
        display="flex"
        flexDirection={'column'}
        alignItems="flex-start"
        gridGap={16}
        justifyContent="center"
        width={'100%'}
      >
        <Font variant="body2" color="textSecondary">
          <Text tId="EDIT_FINANCING_DETAILS_TOOLTIP" />
        </Font>
        {showVehicleAmountControl && <EditVehicleAmountControl currency={currency} disabled={isSimulatorLoading} />}
      </Box>
      <CustomPaper
        style={{
          width: 'auto',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: palette.background.paper,
          padding: 0,
          borderRadius: customBorderRadius.paper,
        }}
      >
        <div
          id={simulatorMountId}
          style={{
            height: '650px',
            overflowY: 'hidden',
            overflowX: 'auto',
          }}
        />
        {!hasSimulatorMounted && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Font variant="body2" color="textSecondary">
              <Text tId="LOADING" />
            </Font>
          </Box>
        )}
      </CustomPaper>
    </Box>
  );
};
